import { forwardRef, useId } from 'react'
import type { InputHTMLAttributes } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/cn'
import { describedBy } from '@/components/ui/Input'

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'id'> {
  label: string
  description?: string
  error?: string
  id?: string
  containerClassName?: string
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  {
    label,
    description,
    error,
    id,
    disabled,
    containerClassName,
    className,
    'aria-describedby': ariaDescribedBy,
    ...props
  },
  ref,
) {
  const generatedId = useId()
  const controlId = id ?? `checkbox-${generatedId}`
  const descriptionId = `${controlId}-description`
  const errorId = `${controlId}-error`

  return (
    <div className={cn('flex flex-col gap-1', containerClassName)}>
      <div className="flex items-start gap-2.5 py-1">
        <span className="relative mt-0.5 flex size-5 shrink-0 items-center justify-center">
          <input
            ref={ref}
            id={controlId}
            type="checkbox"
            disabled={disabled}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy(
              description && descriptionId,
              error && errorId,
              ariaDescribedBy,
            )}
            className={cn(
              'peer absolute inset-0 size-full cursor-pointer appearance-none rounded-[0.35rem]',
              'border bg-[var(--surface-card)] transition-colors',
              'checked:border-[var(--accent)] checked:bg-[var(--accent)]',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]',
              'disabled:cursor-not-allowed disabled:opacity-55',
              error ? 'border-[var(--danger)]' : 'border-[var(--border-strong)]',
              className,
            )}
            {...props}
          />
          <Check
            aria-hidden="true"
            strokeWidth={3}
            className="pointer-events-none size-3.5 text-[var(--accent-contrast)] opacity-0 peer-checked:opacity-100"
          />
        </span>

        <span className="flex min-w-0 flex-col gap-0.5">
          <label
            htmlFor={controlId}
            className={cn(
              // `anywhere` (e não `break-word`) porque nomes de perfil vêm sem
              // espaço — "metodomentesbrilhantes" precisa poder quebrar.
              'cursor-pointer text-sm font-medium leading-5 text-[var(--text-primary)]',
              '[overflow-wrap:anywhere]',
              disabled && 'cursor-not-allowed opacity-60',
            )}
          >
            {label}
          </label>
          {description && (
            <span
              id={descriptionId}
              className="text-xs leading-4 text-[var(--text-muted)] [overflow-wrap:anywhere]"
            >
              {description}
            </span>
          )}
        </span>
      </div>

      {error && (
        <p id={errorId} role="alert" className="text-xs font-medium text-[var(--danger)]">
          {error}
        </p>
      )}
    </div>
  )
})
